import sessionApi from './apiClient';

export interface PrivacyNotice {
  version: string;
  title: string;
  content: string;
  effective_date: string;
}

export interface PrivacyStatusResponse {
  success: boolean;
  has_active_consent: boolean;
  notice: PrivacyNotice | null;
  error?: string;
}

export interface PrivacyComplianceSummary {
  active_notice_version: string | null;
  accepted_users_count: number;
  pending_users_count: number;
  total_active_users_count: number;
  latest_acceptance_timestamp: string | null;
  compliance_rate: number;
}

export interface AcceptedPrivacyUser {
  id: number;
  user_id: number;
  username: string;
  role: string;
  department: string;
  notice_version: string;
  accepted_at: string;
  status: string;
}

export interface PendingPrivacyUser {
  user_id: number;
  username: string;
  role: string;
  department: string;
  account_status: string;
  last_login: string | null;
  required_notice_version: string | null;
}

export interface PrivacyNoticeVersionRecord extends PrivacyNotice {
  id: number;
  is_active: boolean;
  created_at: string;
}

export interface PrivacyConsentDetail {
  id: number;
  username: string;
  full_name: string;
  email: string;
  role: string;
  department: string;
  notice_version: string;
  accepted_at: string;
  ip_address: string | null;
  user_agent: string;
  consent_text_snapshot: string;
  is_withdrawn: boolean;
  withdrawn_at: string | null;
}

export const privacyService = {
  getStatus: async (): Promise<PrivacyStatusResponse> => {
    const response = await sessionApi.get<PrivacyStatusResponse>('/privacy/status/');
    return response.data;
  },

  accept: async (): Promise<PrivacyStatusResponse> => {
    const response = await sessionApi.post<PrivacyStatusResponse>('/privacy/accept/', { accepted: true });
    return response.data;
  },

  getComplianceSummary: async (): Promise<PrivacyComplianceSummary> => {
    const response = await sessionApi.get<{ success: boolean; summary: PrivacyComplianceSummary }>('/privacy/compliance/summary/');
    return response.data.summary;
  },

  getAcceptedUsers: async (): Promise<AcceptedPrivacyUser[]> => {
    const response = await sessionApi.get<{ success: boolean; results: AcceptedPrivacyUser[] }>('/privacy/compliance/accepted-users/');
    return response.data.results;
  },

  getPendingUsers: async (): Promise<PendingPrivacyUser[]> => {
    const response = await sessionApi.get<{ success: boolean; results: PendingPrivacyUser[] }>('/privacy/compliance/pending-users/');
    return response.data.results;
  },

  getNoticeVersions: async (): Promise<PrivacyNoticeVersionRecord[]> => {
    const response = await sessionApi.get<{ success: boolean; results: PrivacyNoticeVersionRecord[] }>('/privacy/compliance/notices/');
    return response.data.results;
  },

  getConsentDetail: async (id: number): Promise<PrivacyConsentDetail> => {
    const response = await sessionApi.get<{ success: boolean; consent: PrivacyConsentDetail }>(`/privacy/compliance/consents/${id}/`);
    return response.data.consent;
  },
};
